"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStudio } from "@/contexts/studio-context";
import { useVoicingPreview } from "@/components/ui/organisms/harmony/use-voicing-preview";
import type { PhraseChord } from "@/components/learn-piece/phrase-chords";
import type { Phrase } from "@/lib/learning-journey/types";
import type { RangeRunner } from "@/lib/learning-journey/runtime/range-runner";

/**
 * Drives the S0 walkthrough: which chord is current, and what sounds.
 *
 * ## Two audio engines, on purpose
 *
 * Hearing **the phrase** goes through `RangeRunner`: it needs the real
 * transport so the roll scrolls and the rhythm is the actual rhythm.
 * Hearing **one chord** goes through `useVoicingPreview`: no count-in, no
 * transport seek, instant. Routing a 400 ms chord through `RangeRunner`
 * would jerk the scroll and add a latency that makes clicking feel broken.
 *
 * ## Hover is silent, click sounds
 *
 * Sound on hover reads as delightful in a design review and becomes
 * unbearable by the third phrase, when the pointer crosses the frieze on
 * its way to the button. Hover only moves the highlight.
 */

/** Gap between chords when stepping through them without a transport. */
const SEQUENCE_STEP_MS = 700;

/** Beat between the panel appearing and the phrase starting to sound. */
const AUTO_PLAY_DELAY_MS = 500;

/**
 * Three indices, not one.
 *
 * A single `activeIndex` conflated "where the music is" with "where the
 * pointer is", so hovering the frieze erased the current chord — sweeping
 * the mouse toward the CTA blinked the highlight across every cell — and
 * clicking changed nothing on screen, because the pointed cell already
 * looked identical to the selected one.
 *
 * The rule the split encodes: **violet is where the music is, white is
 * where your mouse is.** They never fight because they own different
 * visual properties.
 */
export type S0Walkthrough = {
  /** Where the music is: pinned, else playback, else where it came to rest. */
  readonly currentIndex: number | null;
  /**
   * What the focus block details. During playback the audio wins, so the
   * panel can never describe a chord other than the one being heard.
   */
  readonly focusIndex: number | null;
  /** Where the pointer is. Never changes what counts as current. */
  readonly hoveredIndex: number | null;
  /** Cell that just got clicked, for a brief visual echo. */
  readonly flashIndex: number | null;
  /** A phrase playback is in flight. */
  readonly isPlaying: boolean;
  /** True when this piece has no transport, so playback is chord-by-chord. */
  readonly degraded: boolean;
  readonly hover: (index: number | null) => void;
  /** Pin a chord and play it. */
  readonly select: (index: number) => void;
  /** Move the pinned chord by `delta`, wrapping at the edges. */
  readonly step: (delta: number) => void;
  /** Play the phrase again from its start. */
  readonly replay: () => void;
  /** Index the walkthrough considers pinned, for `aria-pressed`. */
  readonly pinnedIndex: number | null;
};

/** How long the clicked chord's keyboard stays lit after a click. */
const FLASH_MS = 300;

export type UseS0WalkthroughInput = {
  readonly phraseChords: readonly PhraseChord[];
  readonly phrase: Phrase;
  readonly rangeRunner: RangeRunner | null;
  readonly rate: number;
  /** Start playing on mount. False when returning to an already-seen phrase. */
  readonly autoPlay: boolean;
  /** Called once a full phrase playback finishes on its own. */
  readonly onPlaybackEnd?: () => void;
};

export function useS0Walkthrough({
  phraseChords,
  phrase,
  rangeRunner,
  rate,
  autoPlay,
  onPlaybackEnd,
}: UseS0WalkthroughInput): S0Walkthrough {
  const [hovered, setHovered] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const [playbackIndex, setPlaybackIndex] = useState<number | null>(null);
  const [restIndex, setRestIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const [flashIndex, setFlashIndex] = useState<number | null>(null);

  const { play } = useVoicingPreview();
  const { studio } = useStudio();
  const sequenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const degraded = rangeRunner === null;

  /**
   * Bring the roll back to the phrase start once it has been heard.
   *
   * Playback runs to `phrase.endTick`, which *is* the next phrase's first
   * tick — so without this the viewport comes to rest on the following
   * phrase. `PhraseChip` reads the viewport, not the FSM, and would then
   * offer "Reprendre / M'entraîner ici" as though the player had scrolled
   * away on purpose. They are also about to learn this phrase, so this is
   * where the roll belongs.
   */
  const returnToPhraseStart = useCallback(() => {
    void studio?.animateScrollToTick(phrase.startTick);
  }, [phrase.startTick, studio]);

  // Keep the latest callback without making the playback effect depend on
  // it: the parent re-creates it on every render, and re-running the
  // effect would restart the audio mid-phrase.
  const onPlaybackEndRef = useRef(onPlaybackEnd);
  useEffect(() => {
    onPlaybackEndRef.current = onPlaybackEnd;
  }, [onPlaybackEnd]);

  // Dernier accord trouvé : la tête de lecture avance, on ne cherche qu'en
  // avant plutôt que de rebalayer la liste à chaque image de l'audition.
  const chordCursorRef = useRef(0);

  const chordIndexAtTick = useCallback(
    (tick: number): number | null => {
      let index = chordCursorRef.current;

      if (index >= phraseChords.length || tick < phraseChords[index].startTick) {
        index = 0;
      }

      for (; index < phraseChords.length; index++) {
        const entry = phraseChords[index];

        if (tick < entry.startTick) {
          break;
        }

        if (tick < entry.endTick) {
          chordCursorRef.current = index;

          return index;
        }
      }

      return null;
    },
    [phraseChords],
  );

  const clearSequence = useCallback(() => {
    if (sequenceTimerRef.current !== null) {
      clearTimeout(sequenceTimerRef.current);
      sequenceTimerRef.current = null;
    }
  }, []);

  /**
   * Chord-by-chord fallback used when no transport is available. It also
   * happens to read better than real playback on fast phrases, where the
   * true rhythm gives no time to look at anything.
   */
  const runSequence = useCallback(() => {
    clearSequence();

    if (phraseChords.length === 0) {
      return;
    }

    setIsPlaying(true);

    const advance = (index: number): void => {
      if (index >= phraseChords.length) {
        setIsPlaying(false);
        setPlaybackIndex(null);
        setRestIndex(phraseChords.length - 1);
        onPlaybackEndRef.current?.();

        return;
      }

      setPlaybackIndex(index);
      play(phraseChords[index].midis);
      sequenceTimerRef.current = setTimeout(
        () => advance(index + 1),
        SEQUENCE_STEP_MS,
      );
    };

    advance(0);
  }, [clearSequence, phraseChords, play]);

  const runPhrase = useCallback(() => {
    if (rangeRunner === null) {
      runSequence();

      return;
    }

    rangeRunner.cancel();
    setIsPlaying(true);
    setRestIndex(null);
    chordCursorRef.current = 0;

    void rangeRunner
      .run({
        fromTick: phrase.startTick,
        toTick: phrase.endTick,
        rate,
        withCountIn: false,
        onTick: (tick) => setPlaybackIndex(chordIndexAtTick(tick)),
      })
      .then((outcome) => {
        setIsPlaying(false);
        setPlaybackIndex(null);

        if (outcome.kind !== "completed") {
          return;
        }

        // Leave the last chord lit rather than going dark: the phrase
        // ended *somewhere*, and that arrival is the thing to remember.
        setRestIndex(phraseChords.length - 1);
        returnToPhraseStart();
        onPlaybackEndRef.current?.();
      })
      .catch(() => {
        setIsPlaying(false);
        setPlaybackIndex(null);
      });
  }, [
    chordIndexAtTick,
    phrase.endTick,
    phrase.startTick,
    phraseChords.length,
    rangeRunner,
    rate,
    returnToPhraseStart,
    runSequence,
  ]);

  // Auto-play once per phrase, after a short beat.
  //
  // The delay is not cosmetic: the panel animates in, and starting audio
  // on the same frame makes the two collide. It also lets the roll settle
  // on the phrase start before the playhead moves.
  //
  // `phrase.id` in the deps (rather than the whole object) keeps a
  // re-render from restarting the audio.
  const autoPlayedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoPlay || phraseChords.length === 0) {
      return;
    }

    if (autoPlayedForRef.current === phrase.id) {
      return;
    }

    autoPlayedForRef.current = phrase.id;
    const timer = setTimeout(runPhrase, AUTO_PLAY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [autoPlay, phrase.id, phraseChords.length, runPhrase]);

  useEffect(() => {
    return () => {
      clearSequence();
      rangeRunner?.cancel();

      if (flashTimerRef.current !== null) {
        clearTimeout(flashTimerRef.current);
      }
    };
  }, [clearSequence, rangeRunner]);

  const select = useCallback(
    (index: number) => {
      const entry = phraseChords[index];

      if (entry === undefined) {
        return;
      }

      clearSequence();
      rangeRunner?.cancel();
      setIsPlaying(false);
      setPlaybackIndex(null);
      setPinned(index);
      play(entry.midis);

      // Visual echo: the preview lasts ~900ms and without this the click
      // produces no on-screen change at all, which reads as a dead control.
      setFlashIndex(index);

      if (flashTimerRef.current !== null) {
        clearTimeout(flashTimerRef.current);
      }

      flashTimerRef.current = setTimeout(() => setFlashIndex(null), FLASH_MS);
    },
    [clearSequence, phraseChords, play, rangeRunner],
  );

  const step = useCallback(
    (delta: number) => {
      if (phraseChords.length === 0) {
        return;
      }

      const current = pinned ?? playbackIndex ?? restIndex ?? 0;
      const next =
        (current + delta + phraseChords.length) % phraseChords.length;

      select(next);
    },
    [phraseChords.length, pinned, playbackIndex, restIndex, select],
  );

  const replay = useCallback(() => {
    setPinned(null);
    setHovered(null);
    runPhrase();
  }, [runPhrase]);

  // Where the music is. Pointer position is deliberately absent: hovering
  // must never move what the frieze calls "current".
  const currentIndex = useMemo(
    () => pinned ?? playbackIndex ?? restIndex,
    [pinned, playbackIndex, restIndex],
  );

  // What the focus block details. While the phrase is sounding, the audio
  // wins over the pointer so the panel cannot describe a chord other than
  // the one being heard.
  const focusIndex = useMemo(
    () => (isPlaying ? playbackIndex : hovered ?? pinned ?? restIndex),
    [isPlaying, playbackIndex, hovered, pinned, restIndex],
  );

  return {
    currentIndex,
    focusIndex,
    hoveredIndex: hovered,
    flashIndex,
    pinnedIndex: pinned,
    isPlaying,
    degraded,
    hover: setHovered,
    select,
    step,
    replay,
  };
}
